import type {
	DashboardUiColumnConfiguration,
	DashboardUiColumnConfigurations,
	DashboardUiLocaleLabel,
	DashboardUiTableConfiguration
} from '@bringg/types';

export type ColumnTableId = keyof DashboardUiColumnConfigurations;
export type ColumnLabelOverrides = DashboardUiColumnConfigurations;

export const ROUTE_MONITOR_TABLE_ID = 'routes_monitoring_table' as const satisfies ColumnTableId;
export const ORDER_MANAGER_TABLE_ID = 'order_explorer_table' as const satisfies ColumnTableId;

export const updateColumnLabelOverride = (
	overrides: ColumnLabelOverrides,
	tableId: ColumnTableId,
	languageCode: string,
	columnId: string,
	value: string
): ColumnLabelOverrides => {
	const updatedOverrides: ColumnLabelOverrides = { ...(overrides || {}) };
	const tableMap = { ...(updatedOverrides[tableId] || {}) };
	const columnMap = { ...(tableMap[columnId] || { title: {} }) };
	const titleMap = { ...(columnMap.title || {}) };

	if (value.trim()) {
		titleMap[languageCode] = value;
	} else {
		delete titleMap[languageCode];
	}

	if (Object.keys(titleMap).length === 0) {
		delete tableMap[columnId];
	} else {
		columnMap.title = titleMap;
		tableMap[columnId] = columnMap;
	}

	if (Object.keys(tableMap).length === 0) {
		delete updatedOverrides[tableId];
	} else {
		updatedOverrides[tableId] = tableMap;
	}

	return updatedOverrides;
};

export const getColumnLabelOverrideValue = (
	overrides: ColumnLabelOverrides | undefined,
	tableId: ColumnTableId,
	languageCode: string,
	columnId: string
): string | undefined => {
	const value = overrides?.[tableId]?.[columnId]?.title?.[languageCode];
	return value && value.trim() ? value : undefined;
};

const hasNoOverrides = (overrides: object): boolean => Object.keys(overrides).length === 0;

const removeLanguageFromTitleMap = (titleMap: DashboardUiLocaleLabel, languageCode: string): DashboardUiLocaleLabel => {
	const nextTitleMap = { ...titleMap };

	delete nextTitleMap[languageCode];

	return nextTitleMap;
};

const removeLanguageFromColumnMap = (
	columnMap: DashboardUiColumnConfiguration,
	languageCode: string
): DashboardUiColumnConfiguration | undefined => {
	const titleMap = columnMap.title || {};

	if (!(languageCode in titleMap)) {
		return columnMap;
	}

	const nextTitleMap = removeLanguageFromTitleMap(titleMap, languageCode);

	return hasNoOverrides(nextTitleMap) ? undefined : { ...columnMap, title: nextTitleMap };
};

const removeLanguageFromTableMap = (
	tableMap: DashboardUiTableConfiguration,
	languageCode: string
): DashboardUiTableConfiguration | undefined => {
	const nextTableMap: DashboardUiTableConfiguration = {};

	Object.entries(tableMap).forEach(([columnId, columnMap]) => {
		const nextColumnMap = removeLanguageFromColumnMap(columnMap, languageCode);

		if (nextColumnMap) {
			nextTableMap[columnId] = nextColumnMap;
		}
	});

	return hasNoOverrides(nextTableMap) ? undefined : nextTableMap;
};

export const removeLanguageFromColumnLabelOverrides = (
	overrides: ColumnLabelOverrides,
	languageCode: string
): ColumnLabelOverrides => {
	const nextOverrides: ColumnLabelOverrides = {};

	(Object.keys(overrides || {}) as ColumnTableId[]).forEach(tableId => {
		const tableMap = overrides[tableId];
		if (!tableMap) return;

		const nextTableMap = removeLanguageFromTableMap(tableMap, languageCode);

		if (nextTableMap) {
			nextOverrides[tableId] = nextTableMap;
		}
	});

	return nextOverrides;
};
